'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { createContext, useContext, useTransition, ReactNode, useEffect } from 'react';

interface LoadingContextType {
  isPending: boolean;
  startLoadingTransition: (callback: () => void) => void;
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

export const LoadingProvider = ({ children }: { children: ReactNode }) => {
  const [isPending, startTransition] = useTransition();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    const normalizedPath = pathname.replace(/\/{2,}/g, '/');

    if (normalizedPath !== pathname) {
      const query = searchParams.toString();
      const nextUrl = query ? `${normalizedPath}?${query}` : normalizedPath;

      router.replace(nextUrl, { scroll: false });
    }
  }, [pathname, searchParams, router]);

  return (
    <LoadingContext.Provider value={{ isPending, startLoadingTransition: startTransition }}>
      {children}
    </LoadingContext.Provider>
  );
};

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};
